import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Mail, Save, Plus, Trash2, Send, RefreshCw, Bell } from 'lucide-react';

export default function EmailSettingsPage() {
  const [settings, setSettings] = useState({ enabled: true, recipients: [] });
  const [inappSettings, setInappSettings] = useState({ enabled: true });
  const [savingInapp, setSavingInapp] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [units, setUnits] = useState([]);
  const [resendUnit, setResendUnit] = useState('');
  const [resendStageType, setResendStageType] = useState('progress');
  const [resendStageNum, setResendStageNum] = useState('1');
  const [sending, setSending] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);

  useEffect(() => {
    api.get('/admin/email-settings').then(r => setSettings(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get('/admin/inapp-notification-settings').then(r => setInappSettings(r.data)).catch(() => {});
    api.get('/units').then(r => setUnits(r.data)).catch(() => {});
  }, []);

  const toggleInapp = async (enabled) => {
    setSavingInapp(true);
    setInappSettings({ enabled });
    try {
      await api.put('/admin/inapp-notification-settings', { enabled });
      toast.success(enabled ? 'In-app notifications enabled' : 'In-app notifications disabled');
    } catch {
      toast.error('Failed to update in-app notification setting');
      setInappSettings({ enabled: !enabled });
    } finally { setSavingInapp(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/email-settings', settings);
      toast.success('Email settings saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const addRecipient = () => {
    if (!newEmail.trim() || !newEmail.includes('@')) { toast.error('Enter a valid email'); return; }
    if (settings.recipients.includes(newEmail.trim())) { toast.error('Already added'); return; }
    setSettings(prev => ({ ...prev, recipients: [...prev.recipients, newEmail.trim()] }));
    setNewEmail('');
  };

  const removeRecipient = (email) => {
    setSettings(prev => ({ ...prev, recipients: prev.recipients.filter(r => r !== email) }));
  };

  const handleResend = async () => {
    if (!resendUnit) { toast.error('Select a unit'); return; }
    setSending(true);
    try {
      await api.post(`/admin/resend-report/${resendUnit}/${resendStageType}/${resendStageNum}`);
      toast.success('Report email sent');
    } catch { toast.error('Send failed'); }
    finally { setSending(false); }
  };

  const handleSendDigest = async () => {
    setSendingDigest(true);
    try {
      await api.post('/admin/send-weekly-digest');
      toast.success('Weekly digest sent');
    } catch { toast.error('Digest send failed'); }
    finally { setSendingDigest(false); }
  };

  if (loading) return <Layout><div className="text-center py-16 text-slate-400">Loading...</div></Layout>;

  return (
    <Layout>
      <div className="space-y-6" data-testid="email-settings-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              <Mail className="inline h-5 w-5 mr-2 text-blue-600" />Sign-off Email Settings
            </h1>
            <p className="text-sm text-slate-500 mt-1">Configure automatic sign-off report emails</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-email-settings">
            <Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : 'Save'}
          </Button>
        </div>

        {/* Enable/disable */}
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Email Notifications</h3>
              <p className="text-sm text-slate-500">Send PDF report emails when stages become complete</p>
            </div>
            <Switch checked={settings.enabled} onCheckedChange={v => setSettings(prev => ({ ...prev, enabled: v }))} data-testid="email-toggle" />
          </div>
        </div>

        {/* In-App Notifications toggle */}
        <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="inapp-notif-block">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                <Bell className="inline h-4 w-4 mr-1.5 text-slate-500" />In-App Notifications
              </h3>
              <p className="text-sm text-slate-500">Alert Admin, Project Manager, and Site Manager when any stage turns <span className="text-emerald-600 font-medium">GREEN</span> or <span className="text-red-600 font-medium">RED</span></p>
              <p className="text-[11px] text-slate-400 mt-1">Shown as a bell icon with badge; click to jump to the unit and stage</p>
            </div>
            <Switch checked={inappSettings.enabled} disabled={savingInapp} onCheckedChange={toggleInapp} data-testid="inapp-notif-toggle" />
          </div>
        </div>

        {/* Recipients */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Recipients</h3>
          <div className="space-y-2">
            {settings.recipients.map(email => (
              <div key={email} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border">
                <span className="text-sm text-slate-800">{email}</span>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => removeRecipient(email)} data-testid={`remove-recipient-${email.replace(/[@.]/g, '-')}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="email@example.com" className="flex-1" onKeyDown={e => e.key === 'Enter' && addRecipient()} data-testid="new-recipient-input" />
            <Button variant="outline" onClick={addRecipient} data-testid="add-recipient-button"><Plus className="h-4 w-4 mr-1" />Add</Button>
          </div>
        </div>

        {/* Resend report */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            <Send className="inline h-4 w-4 mr-1.5" />Resend Report
          </h3>
          <p className="text-sm text-slate-500">Manually send a stage completion report</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Select value={resendUnit} onValueChange={setResendUnit}>
              <SelectTrigger className="flex-1" data-testid="resend-unit-select"><SelectValue placeholder="Select unit..." /></SelectTrigger>
              <SelectContent>{units.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={resendStageType} onValueChange={setResendStageType}>
              <SelectTrigger className="w-32" data-testid="resend-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="progress">Progress</SelectItem>
                <SelectItem value="qa">QA</SelectItem>
              </SelectContent>
            </Select>
            <Select value={resendStageNum} onValueChange={setResendStageNum}>
              <SelectTrigger className="w-28" data-testid="resend-stage-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Stage 1</SelectItem>
                <SelectItem value="2">Stage 2</SelectItem>
                <SelectItem value="3">Stage 3</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleResend} disabled={sending} className="bg-blue-600 hover:bg-blue-700" data-testid="resend-button">
              <Send className="h-4 w-4 mr-1" />{sending ? 'Sending...' : 'Send'}
            </Button>
          </div>
        </div>

        {/* Weekly Digest */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                <RefreshCw className="inline h-4 w-4 mr-1.5" />Weekly Digest
              </h3>
              <p className="text-sm text-slate-500">Automated every Monday at 8:00 AM UTC covering the previous week</p>
              <p className="text-xs text-slate-400 mt-1">Includes: overall progress %, completed stages, issues summary, unit status overview, and PDF attachment</p>
            </div>
            <Button onClick={handleSendDigest} disabled={sendingDigest} className="bg-blue-600 hover:bg-blue-700" data-testid="send-digest-button">
              <Send className="h-4 w-4 mr-1" />{sendingDigest ? 'Sending...' : 'Send Now'}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
